import { validateSync } from 'class-validator';
import csv from 'csvtojson';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { C } from 'ts-toolbelt';
import { API } from '~/api';
import { ImportResult } from '~/api/api.declaration';
import { plainToDTO } from '~/shared/fields';
import { Async } from '~/shared/tools/async';

export abstract class ImportModel<T extends object> {
	readonly async = new Async();

	@observable.ref data = undefined as T[] | undefined;
	@observable error = undefined as string | undefined;
	@observable errors = 0;
	@observable.ref result = undefined as API.ImportResult | undefined;

	private fileName!: string;

	@computed get canImport() {
		return !!this.data?.length && !this.errors && !this.async.loading && !this.result;
	}

	constructor(
		protected dtoClass: C.Class<[], T>,
		protected postFunction: (data: { items: T[]; fileName: string }) => Promise<{ data: ImportResult }>,
	) {
		makeObservable(this);
	}

	abstract getItemFromRow(row: any): T;

	abstract downloadTemplate(): void;

	protected buildAndDownloadCSV(fileName: string, example: { [key: string]: any }) {
		const headers = Object.keys(example);
		const values = headers.map(key => {
			if (Array.isArray(example[key])) {
				if (typeof example[key].at(0) === 'number') return JSON.stringify(example[key]);
				else return `{${example[key]}}`;
			}
			return example[key];
		});

		const csvEscape = (v: any) => {
			if (v === undefined || v === null) return '';
			const s = String(v);
			if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
			return s;
		};

		const rows: string[] = [];
		rows.push(headers.join(','));
		if (values) rows.push(values.map(csvEscape).join(','));
		const content = rows.join('\n');
		const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = fileName;
		document.body.appendChild(a);
		a.click();
		a.remove();
		setTimeout(() => URL.revokeObjectURL(url), 1000);
	}

	@action.bound reset() {
		this.data = undefined;
		this.error = undefined;
		this.errors = 0;
		this.result = undefined;
	}

	loadCSV = async (file: File) => {
		try {
			this.fileName = file.name;
			this.reset();
			const content = await file.text();
			const rows = await csv().fromString(content);
			this.convertImportRows(rows);
		} catch (e: any) {
			console.error(e);
			runInAction(() => {
				this.error = e.message;
				this.errors = 1;
			});
		}
	};

	saveImported = () => {
		return this.async.call(async () => {
			if (this.data) {
				const rv = await this.postFunction({ items: this.data, fileName: this.fileName });
				return () => (this.result = rv.data);
			}
		});
	};

	@action protected convertImportRows(rows: any[]) {
		let i = 2;
		const results = [];
		for (const row of rows) {
			try {
				const src = this.getItemFromRow(row);
				const dto = plainToDTO(this.dtoClass, src);
				const errs = validateSync(dto);
				if (errs.length) {
					this.error = `At line ${i}: ` + errs[0].toString();
					this.errors++;
				} else {
					results.push(dto);
				}
				i++;
			} catch (e: any) {
				console.log(e);
				throw new Error(`At line ${i}: ${e.message}`);
			}
		}
		console.log({ results });
		this.data = results;
	}

	protected removeBrackets(str: string | undefined) {
		str = str?.trim() ?? '';
		if (!str) return undefined;
		if (str.startsWith('{')) str = str.slice(1);
		if (str.endsWith('}')) str = str.slice(0, -1);
		return str;
	}

	protected parseStringArray<T extends string>(src: any): T[] | undefined {
		if (src === undefined || src === null) return undefined;
		const rv = this.removeBrackets(src)
			?.split(',')
			.map(v => v.trim())
			.filter(Boolean);
		if (!rv) return undefined;
		if (Array.isArray(rv)) return rv as T[];
		throw new Error(`Invalid array of strings "${src}"`);
	}

	protected parseNumber(src: string | number | undefined | null): number | undefined | null {
		if (src === undefined || src === null) return undefined;
		if (src === 'null') return null;
		if (typeof src === 'number') return src;
		if (src.trim() === '') return undefined;
		const num = +src;
		if (isFinite(num)) return num;
		throw new Error(`Invalid number "${src}"`);
	}

	protected parseNumberArray<T extends number>(src: any): T[] | undefined {
		if (src === undefined || src === null || src === '') return undefined;
		try {
			const rv = JSON.parse(src);
			if (Array.isArray(rv) && rv.every(i => typeof i === 'number')) return rv as T[];
		} catch (err) {
			throw new Error(`Invalid array of numbers "${src}"`);
		}
	}

	protected parseBoolean(src: any): boolean | undefined {
		if (src === undefined || src === null || src === 'null') return undefined;
		if (typeof src === 'boolean') return src;
		if (typeof src === 'string') {
			if (src.toLowerCase().trim().startsWith('t')) return true;
			if (src.toLowerCase().trim().startsWith('f')) return false;
		}
		throw new Error(`Invalid boolean "${src}"`);
	}

	protected parseEnum<T extends object>(enumType: T, src: any): keyof T | undefined {
		if (src === undefined || src === null || src === 'null' || src === '') return undefined;
		if (typeof src === 'string') {
			const rv = (enumType as any)[src.trim().toUpperCase()];
			if (rv !== undefined) return rv;
		}
		throw new Error(`Invalid enum "${src}"`);
	}

	protected parseEnumArray<T extends object, E = keyof T>(enumType: T, src: any): E[] | undefined {
		if (src === undefined || src === null) return undefined;
		const rv = this.removeBrackets(src)
			?.split(',')
			.map(v => v.trim().toUpperCase())
			.filter(Boolean);
		if (!rv) return undefined;
		if (Array.isArray(rv) && rv.every(key => (enumType as any)[key] !== undefined)) {
			return rv as E[];
		}
		throw new Error(`Invalid array of enums "${src}"`);
	}
}
